/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: OpenAIVoiceConfiguration.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Voice configuration component for OpenAI TTS

 🔧 Notes:
    - Handles model, voice, response format, speed, and instructions
    - Similar structure to AzureVoiceConfiguration

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { Volume2, RotateCcw } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import Select from '../../../../components/ui/select';
import { useI18n } from '../../../../hooks/useI18n';
import { AiProfile, OpenAIVoiceConfig } from '../../types';

interface OpenAIVoiceConfigurationProps {
  formData: AiProfile;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInputChange: (field: string, value: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedProvider: any;
  onTestVoice?: () => void;
}

const OPENAI_TTS_MODELS = [
  { value: 'tts-1', label: 'TTS-1' },
  { value: 'tts-1-hd', label: 'TTS-1 HD' },
  { value: 'gpt-4o-mini-tts', label: 'GPT-4o Mini TTS' }
];

const OPENAI_VOICES = [
  { value: 'alloy', label: 'Alloy' },
  { value: 'ash', label: 'Ash' },
  { value: 'ballad', label: 'Ballad' },
  { value: 'cedar', label: 'Cedar' },
  { value: 'coral', label: 'Coral' },
  { value: 'echo', label: 'Echo' },
  { value: 'fable', label: 'Fable' },
  { value: 'marin', label: 'Marin' },
  { value: 'nova', label: 'Nova' },
  { value: 'onyx', label: 'Onyx' },
  { value: 'sage', label: 'Sage' },
  { value: 'shimmer', label: 'Shimmer' },
  { value: 'verse', label: 'Verse' }
];

export default function OpenAIVoiceConfiguration({
  formData,
  onInputChange,
  selectedProvider,
  onTestVoice
}: OpenAIVoiceConfigurationProps) {
  const { t } = useI18n();

  // Internal state for voice configuration
  const [voiceConfig, setVoiceConfig] = React.useState<OpenAIVoiceConfig>({
    model: '',
    voice: '',
    responseFormat: 'mp3',
    speed: 1.0,
    instructions: ''
  });

  // Sync with formData.voice_config changes
  React.useEffect(() => {
    try {
      if (formData.voice_config) {
        const parsed =
          typeof formData.voice_config === 'string'
            ? JSON.parse(formData.voice_config)
            : (formData.voice_config as OpenAIVoiceConfig);

        // Only update if the parsed data has a voice field (to avoid overwriting with incomplete data)
        if (parsed && parsed.voice !== undefined && parsed.voice !== null && parsed.voice !== '') {
          setVoiceConfig(parsed);
        }
      }
    } catch (error) {
      console.error('Error parsing voice_config:', error);
    }
  }, [formData.voice_config]);

  // Initialize only once on mount if voice_config missing
  React.useEffect(() => {
    if (!formData.voice_config && formData.model) {
      const initialConfig: OpenAIVoiceConfig = {
        model: formData.model,
        voice: 'alloy', // solo para nuevos
        responseFormat: 'mp3',
        speed: 1.0,
        instructions: ''
      };
      setVoiceConfig(initialConfig);
      onInputChange('voice_config', JSON.stringify(initialConfig));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once

  // Update both voice_config and model field for OpenAI TTS
  const handleModelChange = (value: string) => {
    const newConfig = {
      ...voiceConfig,
      model: value,
      // Preserve existing voice if present, otherwise set default
      voice: voiceConfig.voice || 'alloy'
    };
    setVoiceConfig(newConfig);
    onInputChange('voice_config', JSON.stringify(newConfig));
    // Also update the model field for compatibility with backend validation
    onInputChange('model', value);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
        {/* Row 1: Model and Voice */}
        <Label htmlFor="tts_model" className="text-left" required>
          {t('aiProfiles.fields.voiceModel.label')}
        </Label>
        <div className="col-span-1">
          <Select
            key={voiceConfig.model} // Force re-render if value changes
            value={voiceConfig.model || undefined}
            onValueChange={handleModelChange}
            options={OPENAI_TTS_MODELS}
            placeholder={t('aiProfiles.fields.voiceModel.placeholder')}
          />
        </div>
        <Label htmlFor="tts_voice" className="text-left" required>
          {t('aiProfiles.fields.voice.label')}
        </Label>
        <div className="col-span-1">
          <Select
            key={voiceConfig.voice || 'voice-select'} // Force re-render if value changes
            value={voiceConfig.voice || undefined}
            onValueChange={(value) => {
              const newConfig = {
                ...voiceConfig,
                voice: value
              };
              setVoiceConfig(newConfig);
              onInputChange('voice_config', JSON.stringify(newConfig));
            }}
            options={OPENAI_VOICES}
            placeholder="Select voice"
          />
        </div>

        {/* Row 2: Speed with Slider */}
        <Label htmlFor="tts_speed" className="text-left">
          Speed: {(voiceConfig.speed || 1.0).toFixed(2)}x
        </Label>
        <div className="col-span-1 flex items-center gap-2">
          <input
            type="range"
            min="0.25"
            max="4.0"
            step="0.05"
            value={voiceConfig.speed || 1.0}
            onChange={(e) => {
              const newConfig = {
                ...voiceConfig,
                speed: parseFloat(e.target.value)
              };
              setVoiceConfig(newConfig);
              onInputChange('voice_config', JSON.stringify(newConfig));
            }}
            className="flex-1 h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${((voiceConfig.speed || 1.0) - 0.25) * 100 / 3.75}%, hsl(var(--secondary)) ${((voiceConfig.speed || 1.0) - 0.25) * 100 / 3.75}%, hsl(var(--secondary)) 100%)`
            }}
          />
          {(voiceConfig.speed || 1.0) !== 1.0 && (
            <button
              type="button"
              onClick={() => {
                const newConfig = {
                  ...voiceConfig,
                  speed: 1.0
                };
                setVoiceConfig(newConfig);
                onInputChange('voice_config', JSON.stringify(newConfig));
              }}
              className="p-1.5 rounded transition-colors text-orange-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950 flex-shrink-0"
              title="Reset to default (1.0x)"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="col-span-2"></div>

        {/* Row 3: Instructions (full width) */}
        <Label htmlFor="tts_instructions" className="text-left">
          Instructions (Optional)
        </Label>
        <div className="col-span-3">
          <textarea
            id="tts_instructions"
            value={voiceConfig.instructions || ''}
            onChange={(e) => {
              const newConfig = {
                ...voiceConfig,
                instructions: e.target.value
              };
              setVoiceConfig(newConfig);
              onInputChange('voice_config', JSON.stringify(newConfig));
            }}
            placeholder="e.g., Speak with a friendly tone, medium pace, emphasis on the last word of each sentence."
            className="w-full min-h-[80px] px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-y bg-background text-foreground"
            rows={3}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Control tone, emotion, pace, and style of the speech
          </p>
        </div>

        {/* Test Voice Button */}
        {onTestVoice && (
          <div className="col-span-4 mt-4">
            <Button
              type="button"
              onClick={() => {
                onTestVoice();
              }}
              disabled={!voiceConfig.model || !voiceConfig.voice}
              variant="outline"
              className="w-full"
            >
              <Volume2 className="h-4 w-4 mr-2" />
              {t('aiProfiles.fields.testVoice.label')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

